import { ApiPost, ApiGet } from './Common.ts'
import { GetWebConnectorUrl } from '@/stores/SettingsStore.ts'
import type {
  IPwsLeadGetListResponse,
  IPwsLeadAcceptResponse,
  IPwsLeadRejectResponse,
  IPwsLeadReferResponse,
  IPwsLeadReferToGetListResponse
} from '@/types/LeadTypes.ts'


export async function PwsLeadGetList(emailAddress?: string): Promise<IPwsLeadGetListResponse> {
  const url = await GetWebConnectorUrl('PwsLeadGetList')

  // Prepare the filter
  const filter: any = {
    ResponseFilter: {
      Where: {
        OperatorType: "And",
        Operands: [
          {
            Field: "ASSIGNEDDATE",
            FilterType: "IsNotNull"
          }
        ]
      }
    }
  };

  // If emailAddress is provided, add it to the filter
  let response;
  if (emailAddress) {
    filter.ResponseFilter.Where.Operands.push({
      Field: "EMAILADDRESS",
      Value: emailAddress,
      FilterType: "Equal"
    });
    response = await ApiPost(url, filter);
  } else {
    // If no emailAddress provided, just filter for non-null assignedDate
    response = await ApiPost(url, filter);
  }

  // Process the response to ensure sfaLeadMstGuid is always a string
  if (response && response.items && Array.isArray(response.items)) {
    console.log('Lead items received:', response.items.length);

    // Convert sfaLeadMstGuid to string for each item
    response.items = response.items.map((item: { sfaLeadMstGuid: any }) => {
      if (item.sfaLeadMstGuid !== null && item.sfaLeadMstGuid !== undefined) {
        // Ensure sfaLeadMstGuid is a string
        item.sfaLeadMstGuid = String(item.sfaLeadMstGuid);
      }
      return item;
    });
  }

  return response;
}

/**
 * Accept a lead
 * @param sfaLeadMstGuid The unique identifier of the lead to accept
 * @returns Promise with the response
 */
export async function PwsLeadAccept(sfaLeadMstGuid: string): Promise<IPwsLeadAcceptResponse> {
  const url = await GetWebConnectorUrl('PwsLeadAccept')

  console.log('PwsLeadAccept request:', { sfaLeadMstGuid });

  return ApiPost(url, {
    SfaLeadMstGuid: sfaLeadMstGuid
  })
}

/**
 * Reject a lead
 * @param sfaLeadMstGuid The unique identifier of the lead to reject
 * @returns Promise with the response
 */
export async function PwsLeadReject(sfaLeadMstGuid: string): Promise<IPwsLeadRejectResponse> {
  const url = await GetWebConnectorUrl('PwsLeadReject')

  console.log('PwsLeadReject request:', { sfaLeadMstGuid });

  return ApiPost(url, {
    SfaLeadMstGuid: sfaLeadMstGuid
  })
}

/**
 * Refer a lead to another consultant
 * @param sfaLeadMstGuid The unique identifier of the lead to refer
 * @param referBeeNumber The bee number of the consultant to refer the lead to
 * @returns Promise with the response
 */
export async function PwsLeadRefer(sfaLeadMstGuid: string, referBeeNumber: number): Promise<IPwsLeadReferResponse> {
  const url = await GetWebConnectorUrl('PwsLeadRefer')

  console.log('PwsLeadRefer request:', { sfaLeadMstGuid, referBeeNumber });

  return ApiPost(url, {
    SfaLeadMstGuid: sfaLeadMstGuid,
    referBeeNumber: referBeeNumber
  })
}

/**
 * Get a list of consultants to refer leads to
 * @returns Promise with the list of consultants
 */
export async function PwsLeadReferToGetList(): Promise<IPwsLeadReferToGetListResponse> {
  const url = await GetWebConnectorUrl('PwsLeadReferToGetList')
  return ApiGet(url, {})
}

/**
 * Get lead note
 * @param sfaLeadMstGuid The unique identifier of the lead
 * @returns Promise with the lead note
 */
export async function PwsLeadNoteGet(sfaLeadMstGuid: string): Promise<any> {
  const url = await GetWebConnectorUrl('PwsLeadNoteGet')
  return ApiGet(url, { SfaLeadMstGuid: sfaLeadMstGuid })
}

/**
 * Save lead note
 * @param sfaLeadMstGuid The unique identifier of the lead
 * @param noteText The note text to save
 * @param codeIdLeadNoteType The note type code (optional)
 * @returns Promise with the response
 */
export async function PwsLeadNoteSave(
  sfaLeadMstGuid: string,
  noteText: string,
  codeIdLeadNoteType?: string
): Promise<any> {
  const url = await GetWebConnectorUrl('PwsLeadNoteSave')
  return ApiPost(url, {
    SfaLeadMstGuid: sfaLeadMstGuid,
    NoteText: noteText,
    CodeIdLeadNoteType: codeIdLeadNoteType || 'USERNOTE'
  })
}
